// v2 signpost — a taller fingerpost: a capped post carrying three pointed
// finger boards at different heights, each aimed its own way around the compass
// (the pointed tip is a flattened 4-sided cone), with an iron collar under the
// fingers. Builds fast, so no scaffolding. Ported 1:1 from age-of-mcp's
// v2/signpost.ts, using the library's shared `foundationSlab` helper.
import * as THREE from "three";
import { defineModel, foundationSlab, type Part } from "../../lib";

const POST = "#6e5230";
const ARM  = "#a8853f";

function post(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.045, h, 0.045), color: POST,
    position: [0, h / 2, 0] };
}

// One pointed finger board at height `y`, aimed at compass angle `a`
// (radians from +z toward +x): a slat plus a flattened cone tip. The tip's
// cone (axis +y) is first pitched to +z (Rx pi/2), then yawed by `a` — euler
// order 'YXZ' applies exactly that composition.
function finger(y: number, a: number): Part[] {
  const dx = Math.sin(a), dz = Math.cos(a);
  return [
    { geometry: new THREE.BoxGeometry(0.03, 0.05, 0.17), color: ARM,
      position: [dx * 0.115, y, dz * 0.115], rotation: [0, a, 0] },
    { geometry: new THREE.ConeGeometry(0.035, 0.06, 4), color: ARM,
      position: [dx * 0.23, y, dz * 0.23],
      rotation: [Math.PI / 2, a, 0], eulerOrder: "YXZ",
      scale: [1, 1, 0.6] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.18, 0.05)];
  if (s === 1) return [post(0.26)];
  if (s === 2) return [post(0.54)];
  return [
    post(0.54),
    // Pyramid cap on the post top.
    { geometry: new THREE.ConeGeometry(0.045, 0.06, 4), color: POST,
      position: [0, 0.57, 0], rotation: [0, Math.PI / 4, 0] },
    // Iron collar under the fingers.
    { geometry: new THREE.BoxGeometry(0.07, 0.03, 0.07), color: "#3c4046",
      position: [0, 0.27, 0] },
    // Three fingers pointing their own ways at three heights.
    ...finger(0.50, Math.PI / 2),
    ...finger(0.41, -Math.PI / 2 + 0.35),
    ...finger(0.32, 0.18),
  ];
}

export const signpostV2 = defineModel({
  id: "signpost-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Signpost (v2)",
  tags: ["building","signpost","low-poly","age-of-mcp","fable"],
  summary: "Low-poly signpost.",
  description: "A post with arms, each carrying a short label pointing a compass direction.",
  author: "age-of-mcp",
  footprint: 0.13,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
